/* eslint-disable @typescript-eslint/no-explicit-any */
import { Add, Delete, Visibility } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery, useTheme, Card, CardContent, CardActions, Chip } from '@mui/material';

import { useEffect, useState, useCallback } from 'react';
import { FormDialog } from './FormDialog';
import { useParams } from 'react-router-dom';
import { IPlants, PlantsServices } from '../../../services/api/plants/PlantsServices';
import { ConfirmationDialog } from '../../shared';
import { formatDateWhitoutHour } from '../../../services/helpers/helpers';
import { useAuthContext } from '../../../context';

export const PlanAndSections = () => {


    const { userData } = useAuthContext();
    const permissoes = userData.permissoes;
    const { dam } = useParams();
    const [data, setData] = useState<IPlants[]>([]);
    const [dialog, setDialog] = useState<boolean>(false);
    const [plants, setPlants] = useState<string | undefined>(undefined);
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));

    const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
    const [uuidToDelete, setUuidToDelete] = useState<string | null>(null);

    const handleDialogChange = () => {
        setDialog(!dialog);
    };

    const readFileAsBase64 = useCallback((file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                if (typeof reader.result === 'string') {
                    const base64String = reader.result.split(',')[1] || reader.result;
                    resolve(base64String);
                } else {
                    reject(new Error('Falha ao ler o arquivo como string Base64.'));
                }
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    }, []);

    const fetchData = useCallback(async () => {
        if (dam) {
            try {
                const response = await PlantsServices.getAll(dam);
                if (response instanceof Error) {
                    return response.message;
                } else {
                    const sortedData = [...response].sort((a, b) => {
                        const dateA = a.data ? new Date(a.data).getTime() : 0;
                        const dateB = b.data ? new Date(b.data).getTime() : 0;
                        return dateB - dateA;
                    });
                    setData(sortedData);
                    const mostRecentItem = getMostRecentItem(response);

                    if (mostRecentItem?.arquivo instanceof File) {
                        const base64 = await readFileAsBase64(mostRecentItem.arquivo);
                        setPlants(base64);
                    } else if (typeof mostRecentItem?.arquivo === 'string') {
                        setPlants(mostRecentItem.arquivo);
                    } else {
                        setPlants(undefined);
                    }
                }
            } catch (error) {
                return error;
            }
        }
    }, [dam, readFileAsBase64]);

    function getMostRecentItem(items: IPlants[]): IPlants | null {
        if (items.length === 0) return null;

        return items.reduce((latest, current) => {
            let latestDate = new Date(0);
            let currentDate = new Date(0);
            if (latest.data) {
                latestDate = new Date(latest.data);
            }

            if (current.data) {
                currentDate = new Date(current.data);
            }
            return latestDate > currentDate ? latest : current;
        });
    }

    const confirmDeletion = async () => {
        if (uuidToDelete) {
            try {
                const response = await PlantsServices.delet(uuidToDelete);
                if (response instanceof Error) {
                    console.error(response.message);
                } else {
                    fetchData(); 
                }
            } catch (error) {
                console.error('Erro ao deletar:', error);
            } finally {
                setIsOpenConfirmDialog(false); 
                setUuidToDelete(null); 
            }
        }
    };

    const handleOpenConfirmDialog = (uuid: string) => {
        setUuidToDelete(uuid);
        setIsOpenConfirmDialog(true); 
    };

    const handleViewImage = (base64Image: string) => {
        if (base64Image) {
            const image = new Image();
            image.src = `data:image/jpeg;base64,${base64Image}`;

            const w = window.open('');
            if (w) {
                w.document.write(image.outerHTML);
                w.document.close();
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const MobilePlantCard = ({ item }: { item: IPlants }) => (
        <Card variant="outlined" sx={{ mb: 2, borderRadius: '15px' }}>
            <CardContent>
                <Box display="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" component="div" sx={{ wordBreak: 'break-word', flex: 1, mr: 1 }}>
                        {item.descricao || 'Sem Descrição'}
                    </Typography>
                    {item.data && (
                        <Chip
                            label={formatDateWhitoutHour(item.data)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                        />
                    )}
                </Box>
            </CardContent>
            <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2, pt: 0 }}>
                {item.arquivo instanceof File ? (
                    <Button
                        startIcon={<Visibility />}
                        onClick={async () => {
                            try {
                                const base64 = await readFileAsBase64(item.arquivo as File);
                                handleViewImage(base64);
                            } catch (error) {
                                return error;
                            }
                        }}
                        size="small"
                        variant="outlined"
                        fullWidth
                    >
                        Ver Imagem
                    </Button>
                ) : typeof item.arquivo === 'string' && item.arquivo ? (
                    <Button
                        startIcon={<Visibility />}
                        onClick={() => handleViewImage(item.arquivo as any)}
                        size="small"
                        variant="outlined"
                        fullWidth
                    >
                        Ver Imagem
                    </Button>
                ) : null}
                <Button
                    color='error'
                    startIcon={<Delete />}
                    onClick={() => handleOpenConfirmDialog(item.uuid)}
                    size="small"
                    variant="outlined"
                    fullWidth
                >
                    Excluir
                </Button>
            </CardActions>
        </Card>
    );

    return (
        <>
            <Box>
                <Box display={'flex'} width={'100%'} justifyContent={'start'} mb={2}>
                    <Typography variant='h5'>Plantas e Seções</Typography>
                </Box>
                <Box display="flex" justifyContent={'center'} alignContent={'center'} mb={smDown ? 2 : 4}>
                    {plants ?
                        <img
                            src={`data:image/jpeg;base64,${plants}`}
                            style={{
                                width: '100%',
                                maxWidth: '100%',
                                height: 'auto',
                                borderRadius: '15px',
                                border: '1px solid #dcdcdc'
                            }}
                        />
                        :
                        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '15px', width: '100%' }}>
                            <Typography color="text.secondary">
                                Nenhuma planta/seção cadastrada.
                            </Typography>
                        </Paper>
                    }
                </Box>
                <Box padding={1}>
                    <Divider sx={{ mb: smDown ? 2 : 3 }} />
                    <Box display={'flex'} width={'100%'} justifyContent={smDown ? 'center' : 'space-between'} alignItems={'center'} flexDirection={smDown ? 'column' : 'row'} mb={2}>
                        <Typography variant='subtitle1' mb={smDown ? 1 : 0}>Histórico de Seção</Typography>
                        {permissoes.includes('add') && 
                            <Button
                                variant='outlined'
                                sx={{ borderRadius: '15px' }}
                                startIcon={<Add />}
                                onClick={handleDialogChange}
                                fullWidth={smDown}
                            >
                                Nova Planta/Seção
                            </Button>
                        }
                    </Box>
                    <Box padding={1}>
                        {smDown ? (
                            data.length > 0 ? (
                                data.map(item => (
                                    <MobilePlantCard key={item.uuid} item={item} />
                                ))
                            ) : (
                                <Paper sx={{ p: 3, textAlign: 'center', borderRadius: '15px' }}>
                                    <Typography color="text.secondary">
                                        Nenhum histórico de planta/seção encontrado.
                                    </Typography>
                                </Paper>
                            )
                        ) : (
                            <TableContainer component={Paper} variant='outlined' sx={{ borderRadius: '15px' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align='center'>Data</TableCell>
                                            <TableCell align='center'>Descrição do arquivo</TableCell>
                                            <TableCell align='center'>Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.map(index => (
                                            <TableRow key={index.uuid}
                                                sx={{ justifyContent: 'center', alignContent: 'center' }}
                                            >
                                                <TableCell align='center'>
                                                    <Typography>{formatDateWhitoutHour(index.data)}</Typography>
                                                </TableCell>
                                                <TableCell align='center'>
                                                    <Typography>{index.descricao}</Typography>
                                                </TableCell>
                                                <TableCell align='center'>
                                                    {index.arquivo instanceof File ? (
                                                        <IconButton
                                                            onClick={async () => {
                                                                try {
                                                                    const base64 = await readFileAsBase64(index.arquivo as File);
                                                                    handleViewImage(base64);
                                                                } catch (error) {
                                                                    return error;
                                                                }
                                                            }}
                                                        >
                                                            <Visibility color='info' />
                                                        </IconButton>
                                                    ) : typeof index.arquivo === 'string' && index.arquivo ? (
                                                        <IconButton onClick={() => handleViewImage(index.arquivo as any)}>
                                                            <Visibility color='info' />
                                                        </IconButton>
                                                    ) : null}
                                                    {permissoes.includes('remove') &&
                                                        <IconButton
                                                            onClick={() => handleOpenConfirmDialog(index.uuid)}
                                                        >
                                                            <Delete color='error' />
                                                        </IconButton>
                                                    }
                                                </TableCell>
                                            </TableRow>

                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Box>

            </Box>
            <FormDialog dialog={dialog} handleDialogChange={handleDialogChange} value={data} fetchData={fetchData} />
            <ConfirmationDialog
                isOpen={isOpenConfirmDialog} 
                onClose={() => setIsOpenConfirmDialog(false)}
                onConfirm={confirmDeletion}
                title="Confirmar Exclusão"
                message="Tem certeza que deseja excluir esta planta/seção? Esta ação não pode ser desfeita."
            />
        </>
    );
};